import { useState, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'

export type UploadStatus =
  | 'idle'
  | 'requesting'
  | 'uploading'
  | 'confirming'
  | 'success'
  | 'error'

interface UploadState {
  status: UploadStatus
  progress: number // 0-100
  error: string | null
  fileId: string | null
}

interface UseFileUploadResult extends UploadState {
  uploadFile: (file: File, sampleId: string) => Promise<string | null>
  reset: () => void
}

export function useFileUpload(): UseFileUploadResult {
  const { apiClient } = useAuth()
  const [state, setState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    error: null,
    fileId: null,
  })

  const uploadFile = useCallback(
    async (file: File, sampleId: string): Promise<string | null> => {
      if (!apiClient) {
        setState({
          status: 'error',
          progress: 0,
          error: 'Not authenticated',
          fileId: null,
        })
        return null
      }

      try {
        // 1. Request presigned URL
        setState({ status: 'requesting', progress: 0, error: null, fileId: null })

        const contentType = file.type || 'application/octet-stream'
        const { uploadUrl, uploadId } = await apiClient.requestUploadUrl(
          file.name,
          contentType,
          file.size,
          sampleId
        )

        // 2. Compute SHA256 hash
        const sha256 = await computeSha256(file)

        // 3. Upload to S3
        setState((s) => ({ ...s, status: 'uploading' }))
        await uploadToS3(uploadUrl, file, contentType, (progress) => {
          setState((s) => ({ ...s, progress }))
        })

        // 4. Confirm upload with backend
        setState((s) => ({ ...s, status: 'confirming', progress: 100 }))
        const { sampleFileId } = await apiClient.confirmUpload(
          uploadId,
          file.name,
          contentType,
          file.size,
          sha256,
          sampleId
        )

        setState({
          status: 'success',
          progress: 100,
          error: null,
          fileId: sampleFileId,
        })
        return sampleFileId
      } catch (err) {
        let message = 'Upload failed'
        if (err instanceof ApiError) {
          message = err.message
        } else if (err instanceof Error) {
          message = err.message
        }
        setState({ status: 'error', progress: 0, error: message, fileId: null })
        return null
      }
    },
    [apiClient]
  )

  const reset = useCallback(() => {
    setState({ status: 'idle', progress: 0, error: null, fileId: null })
  }, [])

  return { ...state, uploadFile, reset }
}

/**
 * Upload file to S3 using a presigned URL with progress tracking.
 */
async function uploadToS3(
  url: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.ontimeout = () => reject(new Error('Upload timed out'))

    xhr.send(file)
  })
}

/**
 * Compute SHA256 hash of a file using the Web Crypto API.
 */
async function computeSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}
